'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRestaurantStore } from '@/stores/restaurants';
import useStore from '@/stores/useStore';
import STOREFRONT from '@/lib/utils/translation';
import SearchCard from '@/components/search/SearchCard';
import { search, shopsInCityService } from '@/clients/customer/restaurants';
import { motion } from 'framer-motion';

interface Props {
  data: any;
  pageType: 'delivery' | 'pickup' | 'all';
  queryParams?: { [key: string]: any };
  params: { [key: string]: string };
  showLoadMore?: boolean;
  searchPath?: string;
}

// Loading Skeleton Component
const RestaurantCardSkeleton = () => (
  <div className="h-[300px] w-full animate-pulse overflow-hidden rounded-[10px] bg-slate-200">
    <div className="h-[180px] rounded-t-[10px] bg-slate-300"></div>
    <div className="space-y-3 p-4">
      <div className="h-4 w-3/4 rounded bg-slate-300"></div>
      <div className="h-3 w-1/2 rounded bg-slate-300"></div>
      <div className="flex items-center justify-between">
        <div className="h-3 w-1/3 rounded bg-slate-300"></div>
        <div className="h-3 w-1/4 rounded bg-slate-300"></div>
      </div>
    </div>
  </div>
);

export default function RestaurantList({
  data,
  pageType,
  queryParams,
  params,
  searchPath = 'restaurants',
}: Props) {
  const restaurantsData = useStore(
    useRestaurantStore,
    (state) => state.restaurantsData
  );

  const isLoading = useStore(useRestaurantStore, (state) => state.isLoading);
  const setData = useRestaurantStore((state) => state.setData);
  const [isMoreLoading, setIsMoreLoading] = useState(false);

  const t = useTranslations();

  // Intersection Observer ref for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isFetching = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track if we're in a tab view
  const [isTabHidden, setIsTabHidden] = useState(false);

  useEffect(() => {
    if (data) {
      setData(data);
    }
  }, [data, setData]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabHidden(document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchMoreData = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isFetching.current || isTabHidden) return;

    // Check if there are more pages available
    if (
      restaurantsData &&
      restaurantsData?.meta?.last_page <= restaurantsData?.meta?.current_page
    ) {
      return;
    }

    isFetching.current = true;
    setIsMoreLoading(true);

    const filterParams = {
      ...queryParams,
      page: restaurantsData ? restaurantsData?.meta?.current_page + 1 : 1,
    };

    const headers = {
      ['Accept-Language']: params?.locale || '',
    };

    try {
      let response;
      if (searchPath === 'city') {
        response = await shopsInCityService(params.slug, filterParams, headers);
      } else {
        response = await search(filterParams, headers);
      }

      const newRestaurants = response.data;
      const updatedResData = {
        ...restaurantsData,
        meta: newRestaurants.meta,
        data: restaurantsData
          ? [...restaurantsData.data, ...newRestaurants.data]
          : newRestaurants.data,
      };

      setData(updatedResData);
      setIsMoreLoading(false);
      isFetching.current = false;
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsMoreLoading(false);
      isFetching.current = false;
    }
  }, [
    restaurantsData,
    queryParams,
    params,
    searchPath,
    setData,
    setIsMoreLoading,
    isTabHidden,
  ]);

  // Improved infinite scroll using Intersection Observer
  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // Only trigger if element is visible, not fetching, and tab is visible
        if (
          entry.isIntersecting &&
          !isFetching.current &&
          !isTabHidden &&
          !isMoreLoading
        ) {
          const hasMorePages =
            restaurantsData &&
            restaurantsData?.meta?.last_page >
              restaurantsData?.meta?.current_page;

          if (hasMorePages) {
            fetchMoreData();
          }
        }
      },
      {
        // Trigger when element is 200px from being visible
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    if (
      !isMoreLoading &&
      !isTabHidden &&
      !isFetching.current &&
      loadMoreElement
    ) {
      observer.observe(loadMoreElement);
    }

    return () => {
      observer.unobserve(loadMoreElement);
    };
  }, [fetchMoreData, restaurantsData, isMoreLoading, isTabHidden]);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  if (isLoading) {
    return (
      <div className="mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3">
        {Array.from({ length: 8 }, (_, i) => (
          <RestaurantCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Check if there are more pages
  const hasMorePages =
    restaurantsData &&
    restaurantsData?.meta?.last_page > restaurantsData?.meta?.current_page;

  return (
    <div ref={containerRef}>
      <motion.div
        className="scroll-container mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {restaurantsData?.data &&
          restaurantsData.data?.map((deliveryData: any) => {
            return (
              <SearchCard
                type={pageType}
                restaurant={deliveryData}
                key={deliveryData.id}
              />
            );
          })}
      </motion.div>

      {/* Intersection Observer trigger element */}
      {hasMorePages && (
        <div ref={loadMoreRef} className="h-4 w-full" aria-hidden="true" />
      )}

      {/* Loading skeleton grid when loading more */}
      {isMoreLoading && hasMorePages && (
        <div className="mb-[34px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <RestaurantCardSkeleton key={`loading-${i}`} />
          ))}
        </div>
      )}

      {/* Loading indicator for better UX */}
      {isMoreLoading && (
        <div className="py-8 text-center">
          <div className="inline-flex items-center gap-3">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-200 border-t-blue-500"></div>
            <span className="text-gray-600">
              {t('common.loading')} shops...
            </span>
          </div>
        </div>
      )}

      {/* End of results - Only show when no more pages */}
      {restaurantsData &&
        restaurantsData?.data?.length > 0 &&
        !hasMorePages && (
          <div className="py-8 text-center">
            <div className="inline-flex items-center gap-3 text-gray-500">
              <div className="h-px w-12 bg-gray-300" />
              <span className="text-sm font-medium">
                {t(STOREFRONT.SEARCH + '.you_have_seen_all')}
              </span>
              <div className="h-px w-12 bg-gray-300" />
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {restaurantsData?.data?.length}{' '}
              {t(STOREFRONT.SEARCH + '.results_found')}
            </p>
          </div>
        )}

      {/* No results */}
      {restaurantsData?.data?.length === 0 && (
        <div className="mx-auto my-[34px] grid w-full place-content-center gap-y-[30px] text-center md:w-[80%] xl:w-[60%]">
          <span className="whitespace-pre-wrap text-h3 font-medium">
            {t(STOREFRONT.SEARCH + '.no_restaurant')}
          </span>
        </div>
      )}
    </div>
  );
}
